import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../lib/api";
import type {
  PayoutMethodSnapshot,
  PayrunHistoryItem,
  PayrunReadyItem,
  PayrunResponse,
} from "../../shared/types";
import { money, num } from "../lib/format";
import { useNative } from "../../shared/phone/media";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Chevron, Segmented } from "../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../shared/phone/Sheet";
import { Icon } from "../components/ui/Icon";
import { MarkPaidSheet, type PaidRecord } from "./partners/MarkPaidSheet";
import { CopyRow, copiedNote, dayLabel, initials, Monogram, plural, SayToast } from "./partners/parts";
import { methodFields, methodLabel, methodSummary } from "./partners/payout";
import "../styles/payrun.css";

type Tab = "topay" | "history";

type RangeKey = "today" | "week" | "7" | "30" | "all" | "custom";

const RANGE_LABEL: Record<Exclude<RangeKey, "custom">, string> = {
  today: "Today",
  week: "This week",
  "7": "Last 7 days",
  "30": "Last 30 days",
  all: "All time",
};

// ── helpers ──────────────────────────────────────────────────────────────

/** Flatten a method into "Account holder: Dana Lopez; Bank: Chase" for CSV export. */
function methodDetailString(method: PayoutMethodSnapshot | null): string {
  return methodFields(method)
    .map(([label, value]) => `${label}: ${value}`)
    .join("; ");
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** A real CSV of the loaded ready list. */
function readyCsv(ready: PayrunReadyItem[]): string {
  const header = ["Partner", "Sales", "Amount (USD)", "Method", "Payout details"];
  const rows = ready.map((r) => [
    r.partnerName,
    String(r.sales),
    (r.amountCents / 100).toFixed(2),
    methodLabel(r.method),
    methodDetailString(r.method),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
}

/** Build the CSV from the loaded ready list and hand the browser a download. */
function exportReadyCsv(ready: PayrunReadyItem[], fileBase: string): void {
  const blob = new Blob([readyCsv(ready)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileBase}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** On a phone, Export opens the share sheet with the file; where it cannot, the file downloads. */
async function shareReadyCsv(ready: PayrunReadyItem[], fileBase: string): Promise<void> {
  const file = new File([readyCsv(ready)], `${fileBase}.csv`, { type: "text/csv" });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: "Pay run" });
      return;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
    }
  }
  exportReadyCsv(ready, fileBase);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function rangeStart(kind: RangeKey): Date | null {
  if (kind === "all" || kind === "custom") return null;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (kind === "today") return start;
  if (kind === "week") {
    const dow = (start.getDay() + 6) % 7; // Monday-based week
    return new Date(start.getFullYear(), start.getMonth(), start.getDate() - dow);
  }
  if (kind === "7") return new Date(start.getFullYear(), start.getMonth(), start.getDate() - 6);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() - 29); // "30"
}

// ── icons ────────────────────────────────────────────────────────────────

function BackIcon() {
  return (
    <svg className="bk-ic" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="s-ic" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20.5 20.5l-4-4" />
    </svg>
  );
}

// ── a copyable payout field ────────────────────────────────────────────────

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1100);
    } catch {
      // Clipboard denied (permissions / insecure context): leave the value on screen to copy by hand.
    }
  };
  return (
    <span className="pr-f">
      <span className="pr-fk">{label}</span>
      <span className="pr-fv">{value}</span>
      <button
        type="button"
        className={`copy-btn${copied ? " copied" : ""}`}
        onClick={copy}
        aria-label={`Copy ${label}`}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </span>
  );
}

// ── mark-paid modal (RECORDS a payment; never moves money) ──────────────────

function MarkPaidModal({
  target,
  onClose,
  onRecorded,
}: {
  target: PayrunReadyItem;
  onClose: () => void;
  onRecorded: (item: PayrunReadyItem, amountCents: number) => void;
}) {
  const [amount, setAmount] = useState((target.amountCents / 100).toFixed(2));
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (busy) return;
    const cents = Math.round(Number.parseFloat(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setErr("Enter an amount greater than zero.");
      return;
    }
    if (cents > target.amountCents) {
      setErr(`That is more than the ${money(target.amountCents)} owed to ${target.partnerName}.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.partners.markPaid(target.partnerId, {
        amountCents: cents,
        reference: reference.trim() || null,
        note: note.trim() || null,
        datePaid: date || null,
        methodSnapshot: target.method,
      });
      onRecorded(target, cents);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div
      className="payrun-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="payrun-modal" role="dialog" aria-modal="true" aria-label={`Mark ${target.partnerName} paid`}>
        <div className="mhead">
          <h3>Mark paid: {target.partnerName}</h3>
          <button type="button" className="mx" onClick={onClose} ref={closeRef} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="mbody">
          <div className="mp-line">
            <span>Send to</span>
            <b>{methodSummary(target.method)}</b>
          </div>
          <div className="field mp-amount">
            <label htmlFor="mp-amount">Amount</label>
            <span className="cur">$</span>
            <input
              id="mp-amount"
              className="inp"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mp-date">Date paid</label>
            <input
              id="mp-date"
              className="inp"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mp-ref">Reference / transaction ID</label>
            <input
              id="mp-ref"
              className="inp"
              value={reference}
              placeholder="Paste the transfer or PayPal ID"
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mp-note">
              Note to partner <span className="opt">optional</span>
            </label>
            <input
              id="mp-note"
              className="inp"
              value={note}
              placeholder="Shows on their payout receipt"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {err && <div className="mp-err">{err}</div>}
          <div className="records-note">
            This records the payment. You pay {target.partnerName} directly with the details above,
            Flashadz never moves the money.
          </div>
        </div>
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={submit} disabled={busy}>
            {busy ? "Recording…" : "Mark as paid"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the pay run view ────────────────────────────────────────────────────────

/** Owner pay run (`/products/:id/payrun`): ready, needs attention, history, mark paid. */
export function PayRun() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const native = useNative();
  const [rangeOpen, setRangeOpen] = useState(false);

  const [data, setData] = useState<PayrunResponse | null>(null);
  const [productName, setProductName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>("topay");
  const [markTarget, setMarkTarget] = useState<PayrunReadyItem | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // history filters (client-side, over the loaded history)
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [range, setRange] = useState<RangeKey>("all");
  const [rangeLabel, setRangeLabel] = useState("All time");
  const [drOpen, setDrOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [custom, setCustom] = useState<{ from: Date; to: Date } | null>(null);
  const drRef = useRef<HTMLDivElement>(null);

  const [toast, setToast] = useState<{ msg: string; color: string; show: boolean }>({
    msg: "",
    color: "var(--violet)",
    show: false,
  });
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, color = "var(--violet)") => {
    setToast({ msg, color, show: true });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2800);
  }, []);
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);

  const load = useCallback(async () => {
    return api.partners.payrun(id);
  }, [id]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    setNotFound(false);
    setData(null);
    setProductName(null);
    load()
      .then((d) => alive && setData(d))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setErr(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => alive && setLoading(false));
    api.products
      .detail(id)
      .then((pd) => alive && setProductName(pd.product.name))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id, load]);

  // Close the date-range menu on an outside click.
  useEffect(() => {
    if (!drOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (drRef.current && !drRef.current.contains(e.target as Node)) setDrOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [drOpen]);

  const refetch = useCallback(async () => {
    const d = await load();
    setData(d);
    return d;
  }, [load]);

  const onRecorded = useCallback(
    (item: PayrunReadyItem, amountCents: number) => {
      // Optimistic: drop the paid partner from Ready and roll the totals now, then reconcile.
      setData((prev) =>
        prev
          ? {
              ...prev,
              ready: prev.ready.filter((r) => r.partnerId !== item.partnerId),
              readyCount: Math.max(0, prev.readyCount - 1),
              readyTotalCents: Math.max(0, prev.readyTotalCents - item.amountCents),
              paidToDateCents: prev.paidToDateCents + amountCents,
            }
          : prev,
      );
      setMarkTarget(null);
      showToast(`Recorded ${money(amountCents)} to ${item.partnerName}`, "var(--win)");
      refetch().catch(() => {});
    },
    [refetch, showToast],
  );

  const markAll = useCallback(async () => {
    if (!data || bulkBusy) return;
    const items = data.ready;
    setBulkBusy(true);
    let done = 0;
    try {
      for (const item of items) {
        await api.partners.markPaid(item.partnerId, {
          amountCents: item.amountCents,
          reference: null,
          note: null,
          datePaid: todayISO(),
          methodSnapshot: item.method,
        });
        done += 1;
      }
      await refetch();
      showToast(`Recorded ${done} ${done === 1 ? "payment" : "payments"}`, "var(--win)");
    } catch (e) {
      await refetch().catch(() => {});
      showToast(
        `Recorded ${done} of ${items.length}, then hit: ${e instanceof ApiError ? e.message : String(e)}`,
        "var(--warn)",
      );
    } finally {
      setBulkBusy(false);
      setBulkConfirm(false);
    }
  }, [data, bulkBusy, refetch, showToast]);

  const pickRange = (kind: Exclude<RangeKey, "custom">) => {
    setRange(kind);
    setRangeLabel(RANGE_LABEL[kind]);
    setCustom(null);
    setDrOpen(false);
  };
  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const from = new Date(customFrom + "T00:00:00");
    const to = new Date(customTo + "T23:59:59");
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
    setCustom({ from, to });
    setRange("custom");
    setRangeLabel(`${customFrom} to ${customTo}`);
    setDrOpen(false);
  };

  const filteredHistory = useMemo((): PayrunHistoryItem[] => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    let from: Date | null = null;
    let to: Date | null = null;
    if (range === "custom" && custom) {
      from = custom.from;
      to = custom.to;
    } else if (range !== "all") {
      from = rangeStart(range);
    }
    return data.history.filter((h) => {
      if (q && !h.partnerName.toLowerCase().includes(q)) return false;
      if (!from && !to) return true;
      if (!h.markedPaidAt) return false;
      const d = new Date(h.markedPaidAt.trim().slice(0, 10) + "T00:00:00");
      if (Number.isNaN(d.getTime())) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [data, query, range, custom]);

  const histTotalCents = useMemo(
    () => filteredHistory.reduce((s, h) => s + h.amountCents, 0),
    [filteredHistory],
  );

  if (native) {
    const partners = `/products/${encodeURIComponent(id)}/partners`;
    const here = `/products/${encodeURIComponent(id)}/payrun`;
    const copied = (label: string, ok: boolean) => showToast(copiedNote(label, ok), ok ? "var(--win)" : "var(--warn)");
    const record = async (r: PaidRecord) => {
      if (!markTarget) return;
      await api.partners.markPaid(markTarget.partnerId, { ...r, methodSnapshot: markTarget.method });
      onRecorded(markTarget, r.amountCents);
    };
    const shownRange = range === "custom" ? `${dayLabel(customFrom) || "Start"} to ${dayLabel(customTo) || "today"}` : rangeLabel;
    const pay = tab === "topay";
    return (
      <div className="pn-screen">
        <PhoneNav
          title="Pay partners"
          large
          back={{ to: partners, label: "Partners" }}
          actions={
            pay && data && data.ready.length > 0 ? (
              <button type="button" className="pn-tbtn" aria-label="Export" onClick={() => void shareReadyCsv(data.ready, `pay-run-${id}-${todayISO()}`)}>
                <Icon name="share" />
              </button>
            ) : undefined
          }
        />
        {loading ? (
          <div className="pn-empty">Loading the pay run…</div>
        ) : notFound ? (
          <div className="pn-empty">
            <b>Product not found</b>
            This product does not exist, or it belongs to another account.
          </div>
        ) : err || !data ? (
          <div className="pn-empty">
            <b>Couldn't load the pay run</b>
            {err}
          </div>
        ) : (
          <div className={pay ? "pn-stack" : "pn-stack tight"}>
            <Segmented<Tab>
              label="Pay partners"
              value={tab}
              onChange={setTab}
              options={[
                { value: "topay", label: `To pay ${num(data.readyCount)}` },
                { value: "history", label: "History" },
              ]}
            />
            {pay ? (
              <>
                <div className="pn-grp">
                  <div className="pn-row static">
                    <span className="pn-ico">
                      <Icon name="coin" />
                    </span>
                    <span className="m">
                      <span className="t">Ready to pay</span>
                      <span className="s">{plural(data.readyCount, "partner")}</span>
                    </span>
                    <span className="pa-v acc">{money(data.readyTotalCents)}</span>
                  </div>
                  <div className="pn-row static">
                    <span className="pn-ico warn">
                      <Icon name="flag" />
                    </span>
                    <span className="m">
                      <span className="t">Needs attention</span>
                      <span className="s">{plural(data.blockedCount, "partner")}</span>
                    </span>
                    <span className="pa-v warn">{money(data.blockedTotalCents)}</span>
                  </div>
                  <div className="pn-row static">
                    <span className="pn-ico plain">
                      <Icon name="check" />
                    </span>
                    <span className="m">
                      <span className="t">Paid to date</span>
                      <span className="s">This program</span>
                    </span>
                    <span className="pa-v">{money(data.paidToDateCents)}</span>
                  </div>
                </div>
                {data.ready.length === 0 ? (
                  <div className="pn-empty">
                    <b>Nothing waiting to pay</b>
                    New rewards land here once they clear the hold and pass the minimum.
                  </div>
                ) : (
                  data.ready.map((p) => (
                    <div className="pn-grp" key={p.partnerId}>
                      <Link className="pn-row pa-payh" to={`/partners/${encodeURIComponent(p.partnerId)}`} state={{ from: here }}>
                        <Monogram name={p.partnerName} />
                        <span className="m">
                          <span className="t">{p.partnerName}</span>
                          <span className="s">
                            {plural(p.sales, "sale")} · {methodLabel(p.method)}
                          </span>
                        </span>
                        <span className="pa-amt">{money(p.amountCents)}</span>
                        <Chevron />
                      </Link>
                      {methodFields(p.method).map(([label, value]) => (
                        <CopyRow key={label} label={label} value={value} onCopied={copied} />
                      ))}
                      {methodFields(p.method).length === 0 ? (
                        <div className="pn-row static">
                          <span className="m">
                            <span className="s">No payout details saved yet.</span>
                          </span>
                        </div>
                      ) : null}
                      <button type="button" className="pn-row pa-mk" onClick={() => setMarkTarget(p)}>
                        <Icon name="check" />
                        Mark paid
                      </button>
                    </div>
                  ))
                )}
                {data.needsAttention.length > 0 ? (
                  <section>
                    <div className="pn-sh">
                      <h2>Needs attention</h2>
                    </div>
                    <div className="pn-grp">
                      {data.needsAttention.map((p) => (
                        <Link
                          key={p.partnerId}
                          className="pn-row wrap pa-attn"
                          to={`/partners/${encodeURIComponent(p.partnerId)}`}
                          state={{ from: here }}
                        >
                          <Monogram name={p.partnerName} />
                          <span className="m">
                            <span className="t">{p.partnerName}</span>
                            <span className="s pa-why">{p.reason}</span>
                            <span className="s">{p.fix}</span>
                          </span>
                          <span className="pa-v">{money(p.amountCents)}</span>
                          <Chevron />
                        </Link>
                      ))}
                    </div>
                  </section>
                ) : null}
              </>
            ) : (
              <>
                <div className="pa-hbar">
                  <label className="pn-search">
                    <Icon name="search" />
                    <input type="search" placeholder="Search payouts" aria-label="Search payout history" value={query} onChange={(e) => setQuery(e.target.value)} />
                  </label>
                  <button
                    type="button"
                    className="pa-range"
                    aria-label={`Show payouts for: ${shownRange}`}
                    onClick={() => {
                      // The phone's date pickers open on days already there: the last 30, up to today.
                      if (!customTo) setCustomTo(todayISO());
                      if (!customFrom) setCustomFrom(new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10));
                      setRangeOpen(true);
                    }}
                  >
                    <Icon name="calendar" />
                    <span>{shownRange}</span>
                    <Icon name="chev" />
                  </button>
                </div>
                <section>
                  {filteredHistory.length > 0 ? (
                    <div className="pn-cap">
                      {plural(filteredHistory.length, "payout")} · {money(histTotalCents)} paid
                    </div>
                  ) : null}
                  <div className="pn-grp">
                    {filteredHistory.length === 0 ? (
                      <div className="pn-row static">
                        <span className="m">
                          <span className="s">
                            {data.history.length === 0
                              ? "No payouts recorded yet. Marking a partner paid records it here."
                              : "No payouts match these days or your search."}
                          </span>
                        </span>
                      </div>
                    ) : (
                      filteredHistory.map((h) => (
                        <div className="pn-row static wrap pa-hrow" key={h.payoutId}>
                          <Monogram name={h.partnerName} />
                          <span className="m">
                            <span className="t">{h.partnerName}</span>
                            <span className="s">
                              {methodLabel(h.method)}
                              {h.reference ? ` · ref ${h.reference}` : ""}
                            </span>
                            {h.note ? <span className="s">Note: {h.note}</span> : null}
                          </span>
                          <span className="pa-hr">
                            <b>{money(h.amountCents)}</b>
                            <span>{dayLabel(h.markedPaidAt, !(h.markedPaidAt ?? "").startsWith(String(new Date().getFullYear())))}</span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
        {pay && data && data.ready.length > 0 ? (
          <div className="pn-foot">
            <button type="button" className="pn-btn pri wide" disabled={bulkBusy} onClick={() => setBulkConfirm(true)}>
              {bulkBusy ? "Recording…" : `Mark all ${data.readyCount} paid, ${money(data.readyTotalCents)}`}
            </button>
          </div>
        ) : null}

        {data ? (
          <ActionSheet
            open={bulkConfirm && !bulkBusy}
            onClose={() => setBulkConfirm(false)}
            title={`Record ${plural(data.readyCount, "payment")}?`}
            message={`You pay each partner directly. This marks ${money(data.readyTotalCents)} as paid.`}
            actions={[{ label: `Mark all ${data.readyCount} paid`, onSelect: () => void markAll() }]}
          />
        ) : null}
        {markTarget ? (
          <MarkPaidSheet
            name={markTarget.partnerName}
            owedCents={markTarget.amountCents}
            method={markTarget.method}
            onClose={() => setMarkTarget(null)}
            onRecord={record}
            onCopied={copied}
          />
        ) : null}
        <Sheet
          open={rangeOpen}
          onClose={() => setRangeOpen(false)}
          title="Show payouts for"
          className="pa-form-sheet"
          foot={
            <button
              type="button"
              className="pn-btn wide"
              disabled={!customFrom || !customTo}
              onClick={() => {
                applyCustom();
                setRangeOpen(false);
              }}
            >
              Show these days
            </button>
          }
        >
          <div className="pn-grp">
            {(["today", "week", "7", "30", "all"] as const).map((k) => (
              <button
                key={k}
                type="button"
                className="pn-row"
                aria-pressed={range === k}
                onClick={() => {
                  pickRange(k);
                  setRangeOpen(false);
                }}
              >
                <span className="m">
                  <span className="t">{RANGE_LABEL[k]}</span>
                </span>
                {range === k ? (
                  <span className="pa-tick">
                    <Icon name="check" />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="pn-cap pa-capgap">Pick the days</div>
          <div className="pn-grp">
            <div className="pn-irow pa-f">
              <label htmlFor="pa-pr-from">From</label>
              <input id="pa-pr-from" type="date" value={customFrom} max={customTo || todayISO()} onChange={(e) => setCustomFrom(e.target.value)} />
            </div>
            <div className="pn-irow pa-f">
              <label htmlFor="pa-pr-to">To</label>
              <input id="pa-pr-to" type="date" value={customTo} min={customFrom || undefined} max={todayISO()} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          </div>
        </Sheet>
        <SayToast toast={{ msg: toast.msg, on: toast.show, done: toast.color !== "var(--warn)" }} />
      </div>
    );
  }

  const backTo = `/products/${encodeURIComponent(id)}/partners`;

  return (
    <section className="payrun view-wrap">
      <button type="button" className="back" onClick={() => navigate(backTo)}>
        <BackIcon />
        Partners
      </button>

      <div className="head">
        <div>
          <div className="eyebrow">{productName ? `${productName} · Partners` : "Partners"}</div>
          <h1>Pay partners</h1>
          <p className="lede">
            You pay each partner directly. Flashadz lists who is owed, gives you their details, and
            records what you mark as paid.
          </p>
        </div>
        {!loading && !notFound && !err && data && tab === "topay" && (
          <div className="head-actions">
            {bulkConfirm ? (
              <div className="pr-confirm">
                <span className="txt">
                  Record {data.readyCount} {data.readyCount === 1 ? "payment" : "payments"}? You pay
                  each partner directly.
                </span>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setBulkConfirm(false)}
                  disabled={bulkBusy}
                >
                  Cancel
                </button>
                <button type="button" className="btn sm" onClick={markAll} disabled={bulkBusy}>
                  {bulkBusy ? "Recording…" : "Confirm"}
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() =>
                    exportReadyCsv(data.ready, `pay-run-${id}-${todayISO()}`)
                  }
                  disabled={data.ready.length === 0}
                >
                  Export pay run
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setBulkConfirm(true)}
                  disabled={data.ready.length === 0}
                >
                  Mark all paid
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {loading && <div className="load-note">Loading the pay run…</div>}

      {!loading && notFound && (
        <div className="missing">
          <h3>Product not found</h3>
          <p>This product does not exist, or it belongs to another account.</p>
        </div>
      )}

      {!loading && err && !notFound && (
        <div className="load-note">Couldn't load the pay run: {err}</div>
      )}

      {!loading && !err && !notFound && data && (
        <>
          <div className="pr-kpis">
            <div className="kpi ready">
              <div className="k">Ready to pay</div>
              <div className="v">{money(data.readyTotalCents)}</div>
              <div className="d">
                {data.readyCount} {data.readyCount === 1 ? "partner" : "partners"}
              </div>
            </div>
            <div className="kpi attn">
              <div className="k">Needs attention</div>
              <div className="v">{money(data.blockedTotalCents)}</div>
              <div className="d">
                {data.blockedCount} {data.blockedCount === 1 ? "partner" : "partners"}
              </div>
            </div>
            <div className="kpi">
              <div className="k">Paid to date</div>
              <div className="v">{money(data.paidToDateCents)}</div>
              <div className="d">this program</div>
            </div>
          </div>

          <div className="pt-bar">
            <div className="tabs" role="tablist" aria-label="Pay run">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "topay"}
                className={tab === "topay" ? "on" : ""}
                onClick={() => setTab("topay")}
              >
                To pay <span className="cnt">{num(data.readyCount)}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "history"}
                className={tab === "history" ? "on" : ""}
                onClick={() => setTab("history")}
              >
                History
              </button>
            </div>

            {tab === "history" && (
              <div className="pt-filters">
                <div className={`searchbox${searchOpen || query ? " open" : ""}`}>
                  <SearchIcon />
                  <input
                    className="search-in"
                    placeholder="Search partners"
                    value={query}
                    aria-label="Search payout history"
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => setSearchOpen(false)}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <div className="daterange" ref={drRef}>
                  <button type="button" className="dr-btn" onClick={() => setDrOpen((o) => !o)}>
                    <span>{rangeLabel}</span>
                    <span className="chev">▾</span>
                  </button>
                  {drOpen && (
                    <div className="dr-menu">
                      {(["today", "week", "7", "30", "all"] as const).map((k) => (
                        <button
                          key={k}
                          type="button"
                          className={range === k ? "on" : ""}
                          onClick={() => pickRange(k)}
                        >
                          {RANGE_LABEL[k]}
                        </button>
                      ))}
                      <div className="dr-custom">
                        <div className="dr-lbl">Custom range</div>
                        <div className="dr-inputs">
                          <input
                            type="date"
                            aria-label="From"
                            value={customFrom}
                            onChange={(e) => setCustomFrom(e.target.value)}
                          />
                          <span>to</span>
                          <input
                            type="date"
                            aria-label="To"
                            value={customTo}
                            onChange={(e) => setCustomTo(e.target.value)}
                          />
                        </div>
                        <button type="button" className="btn sm" onClick={applyCustom}>
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {tab === "topay" ? (
            <>
              <div className="slabel">
                Ready to pay
                <span className="sub">hold cleared, over the minimum, details on file</span>
              </div>
              {data.ready.length === 0 ? (
                <div className="es">
                  <div className="es-ic" aria-hidden="true">
                    ✓
                  </div>
                  <h3>Nothing waiting to pay</h3>
                  <p>
                    Every cleared partner is paid. New rewards land here once they clear the hold and
                    pass the minimum.
                  </p>
                </div>
              ) : (
                data.ready.map((p) => (
                  <div className="pr-row" key={p.partnerId}>
                    <div className="pr-top">
                      <div className="pa">
                        <span className="av">{initials(p.partnerName)}</span>
                        <div>
                          <div className="pn">{p.partnerName}</div>
                          <div className="pr-meta">
                            {num(p.sales)} {p.sales === 1 ? "sale" : "sales"} · last-click
                          </div>
                        </div>
                      </div>
                      <div className="pr-amt">{money(p.amountCents)}</div>
                    </div>
                    <div className="pr-pay">
                      <div className="pr-mhead">
                        <span className="pill aff">{methodLabel(p.method)}</span>
                      </div>
                      {methodFields(p.method).length > 0 ? (
                        <div className="pr-fields">
                          {methodFields(p.method).map(([label, value]) => (
                            <CopyField key={label} label={label} value={value} />
                          ))}
                        </div>
                      ) : (
                        <div className="pr-meta">No payout details saved yet.</div>
                      )}
                    </div>
                    <div className="pr-bot">
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => navigate(`/partners/${encodeURIComponent(p.partnerId)}`)}
                      >
                        View partner
                      </button>
                      <button type="button" className="btn sm" onClick={() => setMarkTarget(p)}>
                        Mark paid
                      </button>
                    </div>
                  </div>
                ))
              )}

              <div className="slabel">
                Needs attention
                <span className="sub">cleared money you cannot send yet</span>
              </div>
              {data.needsAttention.length === 0 ? (
                <div className="pr-empty">Nothing is blocked. Every cleared reward is payable.</div>
              ) : (
                data.needsAttention.map((p) => (
                  <div className="pr-row blocked" key={p.partnerId}>
                    <div className="pr-top">
                      <div className="pa">
                        <span className="av">{initials(p.partnerName)}</span>
                        <div>
                          <div className="pn">{p.partnerName}</div>
                          <div className="pr-reason">{p.reason}</div>
                        </div>
                      </div>
                      <div className="pr-amt dim">{money(p.amountCents)}</div>
                    </div>
                    <div className="pr-bot">
                      <span className="pr-meta">{p.fix}</span>
                      <span className="pill due">Can't pay yet</span>
                    </div>
                  </div>
                ))
              )}
            </>
          ) : (
            <>
              <div className="pr-histsum">
                {filteredHistory.length > 0
                  ? `${filteredHistory.length} ${filteredHistory.length === 1 ? "payout" : "payouts"} · ${money(histTotalCents)} paid`
                  : ""}
              </div>
              {filteredHistory.length === 0 ? (
                <div className="pr-empty">
                  {data.history.length === 0
                    ? "No payouts recorded yet. Marking a partner paid records it here."
                    : "No payouts match this range or search."}
                </div>
              ) : (
                filteredHistory.map((h) => (
                  <div className="pr-row paid" key={h.payoutId}>
                    <div className="pr-top">
                      <div className="pa">
                        <span className="av">{initials(h.partnerName)}</span>
                        <div>
                          <div className="pn">{h.partnerName}</div>
                          <div className="pr-meta">
                            {methodLabel(h.method)}
                            {h.reference ? ` · ref ${h.reference}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="pr-right">
                        <span className="pr-date">{dayLabel(h.markedPaidAt, true)}</span>
                        <div className="pr-amt">{money(h.amountCents)}</div>
                      </div>
                    </div>
                    {h.note && <div className="pr-note">Note to partner: {h.note}</div>}
                  </div>
                ))
              )}
            </>
          )}
        </>
      )}

      {markTarget && (
        <MarkPaidModal
          target={markTarget}
          onClose={() => setMarkTarget(null)}
          onRecorded={onRecorded}
        />
      )}

      <div className={`pt-toast ${toast.show ? "show" : ""}`}>
        <span className="tdot" style={{ background: toast.color }} />
        <span className="msg">{toast.msg}</span>
      </div>
    </section>
  );
}
